import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Mail, Lock, Loader2 } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const DJANGO_BASE_URL = 'https://agritrack.samperlabs.com';

export const Login = () => {
  const { t, locale } = useI18n();
  const [step, setStep] = useState(1); // 1 = email, 2 = password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const translations = {
    en: {
      welcome: 'Welcome back',
      subtitle: 'Sign in to access your farm dashboard',
      emailLabel: 'Email address',
      emailPlaceholder: 'you@farm.com',
      continueBtn: 'Continue',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      signInBtn: 'Sign In',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      register: 'Create account',
      orContinueWith: 'or continue with',
      googleSignIn: 'Continue with Google',
      backToEmail: 'Use different email',
      signingInAs: 'Signing in as',
    },
    tr: {
      welcome: 'Tekrar hoş geldiniz',
      subtitle: 'Çiftlik kontrol panelinize erişmek için giriş yapın',
      emailLabel: 'E-posta adresi',
      emailPlaceholder: 'siz@ciftlik.com',
      continueBtn: 'Devam Et',
      passwordLabel: 'Şifre',
      passwordPlaceholder: 'Şifrenizi girin',
      signInBtn: 'Giriş Yap',
      forgotPassword: 'Şifremi unuttum?',
      noAccount: 'Hesabınız yok mu?',
      register: 'Hesap oluştur',
      orContinueWith: 'veya şununla devam edin',
      googleSignIn: 'Google ile Devam Et',
      backToEmail: 'Farklı e-posta kullan',
      signingInAs: 'Giriş yapılıyor',
    },
  };

  const text = translations[locale] || translations.en;

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      setError(locale === 'tr' ? 'E-posta gerekli' : 'Email is required');
      return;
    }
    setError('');
    setStep(2);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    // Redirect to Django with email and password
    // In real implementation, this would POST to Django
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${DJANGO_BASE_URL}/login/`;
    
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'email';
    emailInput.value = email;
    form.appendChild(emailInput);
    
    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'password';
    passwordInput.value = password;
    form.appendChild(passwordInput);
    
    document.body.appendChild(form);
    form.submit();
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${DJANGO_BASE_URL}/auth/google/`;
  };

  return (
    <div className="min-h-screen bg-surface flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary-light relative overflow-hidden">
        {/* Noise texture */}
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
          }} 
        />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3" data-testid="login-logo">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">AT</span>
            </div>
            <span className="text-2xl font-bold tracking-tight text-white">AgriTrack</span>
          </a>
          
          {/* Center content */}
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight">
              {locale === 'tr' 
                ? 'Çiftliğinizi verilerle yönetin' 
                : 'Manage your farm with data'}
            </h1>
            <p className="text-forest-300 text-lg max-w-md leading-relaxed">
              {locale === 'tr'
                ? 'Hassas hayvancılık yönetimi için tek platform.'
                : 'The single platform for precision livestock management.'}
            </p>
          </div>
          
          {/* Bottom stats */}
          <div className="flex gap-8">
            <div>
              <p className="text-3xl font-black text-white">500+</p>
              <p className="text-forest-400 text-sm">{locale === 'tr' ? 'Aktif Çiftlik' : 'Active Farms'}</p>
            </div>
            <div>
              <p className="text-3xl font-black text-white">50K+</p>
              <p className="text-forest-400 text-sm">{locale === 'tr' ? 'Takip Edilen Hayvan' : 'Animals Tracked'}</p>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-forest-400/10 rounded-full blur-3xl" />
      </div>
      
      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <a href="/" className="lg:hidden flex items-center gap-2 mb-8" data-testid="login-logo-mobile">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AT</span>
            </div>
            <span className="text-xl font-bold tracking-tight text-primary">AgriTrack</span>
          </a>
          
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-black text-primary tracking-tight" data-testid="login-title">
                {text.welcome}
              </h2>
              <p className="text-muted-foreground mt-2">{text.subtitle}</p>
            </div>
            
            {/* Google Sign In */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 border-primary/10 hover:border-primary/20 hover:bg-primary/5 text-primary font-medium rounded-xl transition-all duration-200"
              onClick={handleGoogleSignIn}
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {text.googleSignIn}
            </Button>
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-primary/10"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-surface px-4 text-muted-foreground font-mono tracking-wider">
                  {text.orContinueWith}
                </span>
              </div>
            </div>
            
            {/* Form Steps */}
            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.form
                  key="email-step"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleEmailSubmit}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-primary font-medium">
                      {text.emailLabel}
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder={text.emailPlaceholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-2 border-primary/10 focus:border-primary focus:ring-primary"
                        data-testid="email-input"
                      />
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                    data-testid="continue-btn"
                  >
                    {text.continueBtn}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.form>
              ) : (
                <motion.form
                  key="password-step"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handlePasswordSubmit}
                  className="space-y-4"
                >
                  {/* Email badge */}
                  <div className="flex items-center justify-between p-3 bg-forest-50 rounded-xl border border-forest-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Mail className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{text.signingInAs}</p>
                        <p className="text-sm font-medium text-primary">{email}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-xs text-primary hover:underline font-medium"
                      data-testid="back-to-email"
                    >
                      {text.backToEmail}
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-primary font-medium">
                      {text.passwordLabel}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder={text.passwordPlaceholder}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-2 border-primary/10 focus:border-primary focus:ring-primary"
                        autoFocus
                        data-testid="password-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <a 
                      href={`${DJANGO_BASE_URL}/password-reset/`}
                      className="text-sm text-primary hover:underline font-medium"
                      data-testid="forgot-password-link"
                    >
                      {text.forgotPassword}
                    </a>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0"
                    data-testid="signin-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {locale === 'tr' ? 'Giriş yapılıyor...' : 'Signing in...'}
                      </>
                    ) : (
                      <>
                        {text.signInBtn}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>
            
            {/* Register link */}
            <p className="text-center text-muted-foreground">
              {text.noAccount}{' '}
              <a 
                href="/register" 
                className="text-primary hover:underline font-semibold"
                data-testid="register-link"
              >
                {text.register}
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
